import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TChannel } from '../../_shared/types/channel.type';
import {
  GET_LIST_INTEGRATION_NOLIMIT_ENGAGE_QUERY_KEY,
  TGetListIntegrationNolimitEngageResponse,
} from './get-list-integration-nolimit-engage.api';
import { TGetListIntegrationGoogleReviewResponse } from '../../google-review/api/get-list-integration-google-review.api';

/**
 * Delete Integration Nolimit Engage Input Schema
 */
export const deleteNolimitEngageIntegrationValidationSchema = z.object({
  organizationChannelId: z.string({ required_error: 'Organization channel id is required' }),
});
export type TDeleteNolimitEngageIntegrationValidation = z.infer<typeof deleteNolimitEngageIntegrationValidationSchema>;
export type TDeleteNolimitEngageIntegrationResponse = ApiResponse<TChannel>;

export const deleteNolimitEngageIntegration = async (
  payload: TDeleteNolimitEngageIntegrationValidation
): Promise<TDeleteNolimitEngageIntegrationResponse> => {
  const url = `/integration/engage/org-channel/${payload.organizationChannelId}`;

  return axiosInstance.delete(url);
};

/**
 * Use Delete Nolimit Engage Integration React-Query Hook
 */
type UseDeleteNolimitEngageIntegrationOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteNolimitEngageIntegration, AxiosErrorCustom>;
};

export const useDeleteNolimitEngageIntegration = ({ mutationConfig }: UseDeleteNolimitEngageIntegrationOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteNolimitEngageIntegration,
    ...restConfig,
  });
};

/**
 * Use Delete Nolimit Engage Integration Optimistically React-Query Hook
 */
type UseDeleteNolimitEngageIntegrationOptimisticallyOptions = {
  mutationConfig?: TypedMutationConfig<typeof deleteNolimitEngageIntegration, AxiosErrorCustom>;
};

type TDeleteNolimitEngageIntegrationOptimisticallyContext = {
  previousData: TGetListIntegrationNolimitEngageResponse | undefined;
};

export const useDeleteNolimitEngageIntegrationOptimistically = ({
  mutationConfig,
}: UseDeleteNolimitEngageIntegrationOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: deleteNolimitEngageIntegration,
    onMutate: async (data: TDeleteNolimitEngageIntegrationValidation) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_INTEGRATION_NOLIMIT_ENGAGE_QUERY_KEY] });

      const previousData = queryClient.getQueryData<TGetListIntegrationGoogleReviewResponse>([
        GET_LIST_INTEGRATION_NOLIMIT_ENGAGE_QUERY_KEY,
      ]);

      queryClient.setQueriesData<TGetListIntegrationGoogleReviewResponse>(
        { queryKey: [GET_LIST_INTEGRATION_NOLIMIT_ENGAGE_QUERY_KEY] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            payload: {
              ...oldData.payload,
              rows: oldData.payload.rows.filter(
                (integration) => integration.organizationChannelId !== data.organizationChannelId
              ),
            },
          };
        }
      );

      return { previousData } as TDeleteNolimitEngageIntegrationOptimisticallyContext;
    },

    onError: (_err, _data, context) => {
      if ((context as TDeleteNolimitEngageIntegrationOptimisticallyContext)?.previousData !== undefined) {
        queryClient.setQueryData(
          [GET_LIST_INTEGRATION_NOLIMIT_ENGAGE_QUERY_KEY],
          (context as TDeleteNolimitEngageIntegrationOptimisticallyContext).previousData
        );
      }
    },

    ...restConfig,
  });
};
